import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    X,
    User,
    Briefcase,
    Mail,
    Phone,
    Banknote,
    Percent,
    Clock,
    Camera,
    ArrowRight,
    ArrowLeft,
    CheckCircle2,
    IdCard
} from "lucide-react";
import moment from "moment";
import { ChevronDown } from "lucide-react";
import { addMember, updateMember } from "../../services";
import { toast } from "react-toastify";
import TimeBox from "../../components/TimeBox";

const MemberModal = ({ isOpen, onClose, onRefresh, initialData = null, isEdit = false }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        MemberName: "",
        Designation: "",
        EmailAddress: "",
        PhoneNumber: "",
        CNIC: "",
        Password: "",
        PhotoPath: "",
        MemberStatus: 1, // Default Active
        MemberRole: 1,   // Default Member
        isMonitoringAllowed: true,
        Salary: "",
        BankName: "",
        BankCode: "",
        BankBranchName: "",
        BankAccountTitle: "",
        BankAccountNumber: "",
        taxRate: 0,
        ShiftStart: "09:00:00",
        ShiftEnd: "18:00:00",
        workHours: 9,
        LateCheckInMinutes: 0,
        JoiningDate: new Date(),
        LeaveMins: 0,
    });

    const [showShiftStartPicker, setShowShiftStartPicker] = useState(false);
    const [showShiftEndPicker, setShowShiftEndPicker] = useState(false);

    React.useEffect(() => {
        if (isOpen) {
            if (isEdit && initialData) {
                setFormData({
                    ...initialData,
                    JoiningDate: initialData.JoiningDate ? new Date(initialData.JoiningDate) : new Date(),
                    Salary: initialData.Salary || "",
                    taxRate: initialData.taxRate || 0,
                    LeaveMins: initialData.LeaveMins || 0,
                    workHours: initialData.workHours || 9,
                    LateCheckInMinutes: initialData.LateCheckInMinutes || 0,
                    CNIC: initialData.CNIC ?? initialData.cnic ?? "",
                });
            } else {
                setFormData({
                    MemberName: "",
                    Designation: "",
                    EmailAddress: "",
                    PhoneNumber: "",
                    CNIC: "",
                    Password: "",
                    PhotoPath: "",
                    MemberStatus: 1,
                    MemberRole: 1,
                    isMonitoringAllowed: true,
                    Salary: "",
                    BankName: "",
                    BankCode: "",
                    BankBranchName: "",
                    BankAccountTitle: "",
                    BankAccountNumber: "",
                    taxRate: 0,
                    ShiftStart: "09:00:00",
                    ShiftEnd: "18:00:00",
                    workHours: 9,
                    LateCheckInMinutes: 0,
                    JoiningDate: new Date(),
                    LeaveMins: 0,
                });
            }
            setStep(1);
        }
    }, [isOpen, isEdit, initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : (type === "number" ? Number(value) : value)
        }));
    };

    const nextStep = () => setStep(prev => prev + 1);
    const prevStep = () => setStep(prev => prev - 1);

    const handleSubmit = async () => {
        setLoading(true);
        try {
            // Only send the fields expected by the backend to avoid "property should not exist" errors
            const {
                Id, MemberName, Designation, EmailAddress, PhoneNumber, CNIC, Password, PhotoPath,
                MemberStatus, MemberRole, isMonitoringAllowed, Salary, BankName, BankCode,
                BankBranchName, BankAccountTitle, BankAccountNumber, taxRate,
                ShiftStart, ShiftEnd, workHours, LateCheckInMinutes, JoiningDate, LeaveMins
            } = formData;

            const payload = {
                Id, MemberName, Designation, EmailAddress, PhoneNumber,
                CNIC: typeof CNIC === "string" ? CNIC.trim() : CNIC ?? "",
                Password: Password || undefined, // Send password only if provided
                PhotoPath,
                MemberStatus: Number(MemberStatus),
                MemberRole: Number(MemberRole),
                isMonitoringAllowed,
                Salary: Number(Salary),
                BankName,
                BankCode,
                BankBranchName,
                BankAccountTitle,
                BankAccountNumber,
                taxRate: Number(taxRate),
                ShiftStart,
                ShiftEnd,
                workHours: Number(workHours),
                LateCheckInMinutes: Number(LateCheckInMinutes),
                JoiningDate: JoiningDate instanceof Date ? JoiningDate.toISOString() : JoiningDate,
                LeaveMins: Number(LeaveMins)
            };

            const response = isEdit ? await updateMember(payload) : await addMember(payload);
            if (response.status === 200 || response.status === 201) {
                toast.success(isEdit ? "Member updated successfully!" : "Member added successfully!");
                onRefresh();
                onClose();
            } else {
                toast.error(isEdit ? "Failed to update member" : "Failed to add member");
            }
        } catch (error) {
            console.error("Error adding member:", error);
            const errorMsg = error?.response?.data?.Message || error?.response?.data?.message || "Error adding member";
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const isStepValid = (stepNum) => {
        switch (stepNum) {
            case 1:
                return (
                    formData.MemberName &&
                    formData.Designation &&
                    formData.EmailAddress &&
                    formData.PhoneNumber &&
                    formData.Salary !== "" &&
                    formData.Salary !== null
                );
            case 2:
                return formData.workHours !== "" && formData.workHours !== null;
            case 3:
                return formData.BankName && formData.BankCode && formData.BankAccountTitle && formData.BankAccountNumber && formData.BankBranchName && formData.taxRate !== "" && formData.taxRate !== null;
            default:
                return true;
        }
    };

    const isFormValid = () => {
        return isStepValid(1) && isStepValid(2) && isStepValid(3);
    };

    const formatTimeToDisplay = (timeStr) => {
        if (!timeStr) return "--:--";
        // Convert "HH:mm:ss" to "hh:mm:ss AM/PM"
        try {
            const [hours, minutes, seconds] = timeStr.split(":");
            let hr = parseInt(hours);
            const ampm = hr >= 12 ? "PM" : "AM";
            hr = hr % 12 || 12;
            const displayHr = hr.toString().padStart(2, "0");
            return `${displayHr}:${minutes}:${seconds} ${ampm}`;
        } catch (e) {
            return timeStr;
        }
    };

    if (!isOpen) return null;

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="space-y-5">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <User className="w-5 h-5 text-blue-500" /> Basic Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Full Name *</label>
                                <div className="relative group">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-blue-500" />
                                    <input name="MemberName" value={formData.MemberName} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="John Doe" required />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Designation *</label>
                                <div className="relative group">
                                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-blue-500" />
                                    <input name="Designation" value={formData.Designation} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="Senior Developer" required />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Email Address</label>
                                <div className="relative group">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-blue-500" />
                                    <input name="EmailAddress" value={formData.EmailAddress} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="john@example.com" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Phone Number</label>
                                <div className="relative group">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-blue-500" />
                                    <input name="PhoneNumber" value={formData.PhoneNumber} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="+92 300 1234567" />
                                </div>
                            </div>
                            <div className="space-y-1.5 md:col-span-2">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">CNIC</label>
                                <div className="relative group">
                                    <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-blue-500" />
                                    <input name="CNIC" value={formData.CNIC} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white font-mono tracking-wide" placeholder="35202-1234567-1" autoComplete="off" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Salary *</label>
                                <div className="relative group">
                                    <input
                                        name="Salary"
                                        type="number"
                                        value={formData.Salary}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white"
                                        placeholder="Enter salary amount"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Photo URL</label>
                                <div className="relative group">
                                    <Camera className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-blue-500" />
                                    <input name="PhotoPath" value={formData.PhotoPath} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="https://..." />
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-black/30 rounded-2xl md:col-span-2 border border-dashed border-gray-200 dark:border-gray-800">
                                <input name="isMonitoringAllowed" type="checkbox" checked={formData.isMonitoringAllowed} onChange={handleChange} className="w-5 h-5 rounded-lg border-gray-300 text-blue-600 focus:ring-blue-500" />
                                <div>
                                    <label className="text-sm font-bold text-gray-900 dark:text-white block">Allow Monitoring</label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Enable screen capture and activity tracking for this member</p>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="space-y-5">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <Clock className="w-5 h-5 text-amber-500" /> Shift & Timing
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Shift Start</label>
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setShowShiftStartPicker(true)}
                                        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl text-gray-900 dark:text-white text-sm hover:bg-gray-100 dark:hover:bg-white/5 transition-all text-left"
                                    >
                                        <div className="flex items-center gap-2 font-mono">
                                            <Clock className="w-4 h-4 text-gray-400" />
                                            <span>{formatTimeToDisplay(formData.ShiftStart)}</span>
                                        </div>
                                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showShiftStartPicker ? 'rotate-180' : ''}`} />
                                    </button>

                                    <AnimatePresence>
                                        {showShiftStartPicker && (
                                            <TimeBox
                                                initialTime={formData.ShiftStart}
                                                onApply={(date, shouldClose = true) => {
                                                    const timeStr = moment(date).format("HH:mm:ss");
                                                    setFormData(prev => ({ ...prev, ShiftStart: timeStr }));
                                                    if (shouldClose) setShowShiftStartPicker(false);
                                                }}
                                                onClose={() => setShowShiftStartPicker(false)}
                                            />
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Shift End</label>
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setShowShiftEndPicker(true)}
                                        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl text-gray-900 dark:text-white text-sm hover:bg-gray-100 dark:hover:bg-white/5 transition-all text-left"
                                    >
                                        <div className="flex items-center gap-2 font-mono">
                                            <Clock className="w-4 h-4 text-gray-400" />
                                            <span>{formatTimeToDisplay(formData.ShiftEnd)}</span>
                                        </div>
                                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showShiftEndPicker ? 'rotate-180' : ''}`} />
                                    </button>

                                    <AnimatePresence>
                                        {showShiftEndPicker && (
                                            <TimeBox
                                                initialTime={formData.ShiftEnd}
                                                onApply={(date, shouldClose = true) => {
                                                    const timeStr = moment(date).format("HH:mm:ss");
                                                    setFormData(prev => ({ ...prev, ShiftEnd: timeStr }));
                                                    if (shouldClose) setShowShiftEndPicker(false);
                                                }}
                                                onClose={() => setShowShiftEndPicker(false)}
                                            />
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Daily Work Hours</label>
                                <input name="workHours" type="number" value={formData.workHours} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="9" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Leave Mins (Annual)</label>
                                <input name="LeaveMins" type="number" value={formData.LeaveMins} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="0" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Late CheckIn Mins</label>
                                <input name="LateCheckInMinutes" type="number" value={formData.LateCheckInMinutes} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="0" />
                            </div>
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="space-y-5">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <Banknote className="w-5 h-5 text-emerald-500" /> Financial Details
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Bank Name</label>
                                <input name="BankName" value={formData.BankName} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="Bank Al-Habib" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Bank Code *</label>
                                <input name="BankCode" value={formData.BankCode} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="BAHL" required />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Account Title</label>
                                <input name="BankAccountTitle" value={formData.BankAccountTitle} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="Account Holder Name" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Account Number</label>
                                <input name="BankAccountNumber" value={formData.BankAccountNumber} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="PK00 BAHL..." />
                            </div>
                            <div className="space-y-1.5 md:col-span-2">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Branch Name</label>
                                <input name="BankBranchName" value={formData.BankBranchName} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="Main Branch, Karachi" />
                            </div>
                            {!isEdit && (
                                <div className="space-y-1.5">
                                    <label className="text-sm font-bold text-gray-700 dark:text-gray-400">Tax Rate (%)</label>
                                    <div className="relative group">
                                        <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-blue-500" />
                                        <input name="taxRate" type="number" value={formData.taxRate} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-gray-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm dark:text-white" placeholder="5" />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto">
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                />

                {/* Modal Content */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="relative w-full max-w-2xl bg-white dark:bg-[#0c0c0c] rounded-[2.5rem] shadow-2xl border border-gray-100 dark:border-gray-800"
                >
                    {/* Header */}
                    <div className="px-8 py-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                        <div className="space-y-0.5">
                            <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">
                                {isEdit ? "Edit Member" : "Add New Member"}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Step {step} of 3: {isEdit ? "Update details" : "Fill in the details below"}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2.5 rounded-2xl bg-gray-50 dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-500/10 text-gray-400 hover:text-red-500 transition-all"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full h-1 bg-gray-100 dark:bg-gray-800 flex">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className={`flex-1 transition-all duration-500 ${step >= i ? "bg-blue-600 shadow-[0_0_10px_rgba(37,99,235,0.5)]" : ""}`}
                            />
                        ))}
                    </div>

                    {/* Scrollable Form Body */}
                    <div className="p-8 relative">
                        {renderStep()}
                    </div>

                    {/* Footer Actions */}
                    <div className="px-8 py-6 bg-gray-50/50 dark:bg-black/20 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                        <button
                            onClick={step === 1 ? onClose : prevStep}
                            className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-900 dark:hover:text-white transition-all uppercase tracking-widest"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {step === 1 ? "Cancel" : "Back"}
                        </button>

                        {step < 3 ? (
                            <button
                                onClick={nextStep}
                                disabled={!isStepValid(step)}
                                className="flex items-center gap-2 px-8 py-3 bg-gray-900 dark:bg-white text-white dark:text-black font-black rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-95 uppercase tracking-widest text-sm disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                Continue
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={loading || !isFormValid()}
                                className="flex items-center gap-2 px-10 py-3 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl shadow-xl shadow-blue-600/20 transition-all active:scale-95 uppercase tracking-widest text-sm disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                        <span>Processing...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>{isEdit ? "Update Member" : "Add Member"}</span>
                                        <CheckCircle2 className="w-5 h-5" />
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default MemberModal;
